import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Plus } from 'lucide-react';
import type { BusinessOffer, BusinessOfferBooking } from '../../../types/business';
import TravelerForm from './TravelerForm';
import { gql, useMutation } from '@apollo/client';

const UPDATE_OFFER = gql`
  mutation UpdateOffer($offerId: Int!, $input: BusinessOfferUpdateInput!) {
    updateBusinessOffer(offerId: $offerId, input: $input) {
      id
      status
      bookings {
        id
        travelerInfo
        roomPreferences
        status
      }
    }
  }
`;

const bookingSchema = z.object({
  travelerInfo: z.array(z.object({
    firstName: z.string().min(2, 'Le prénom est requis'),
    lastName: z.string().min(2, 'Le nom est requis'),
    email: z.string().email('Email invalide'),
    phone: z.string().min(10, 'Numéro de téléphone invalide'),
    passport: z.object({
      number: z.string().min(1, 'Le numéro de passeport est requis'),
      expiryDate: z.string().min(1, "La date d'expiration est requise"),
      file: z.string().optional(),
    }),
  })),
  roomPreferences: z.array(z.object({
    type: z.enum(['single', 'double', 'triple', 'suite']),
    count: z.number().min(1),
  })),
  specialRequests: z.string().optional(),
});

type BookingFormData = z.infer<typeof bookingSchema>;

interface EditOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  offer: BusinessOffer;
}

export default function EditOfferModal({
  isOpen,
  onClose,
  offer,
}: EditOfferModalProps) {
  const [updateOffer, { loading }] = useMutation(UPDATE_OFFER, {
    onCompleted: () => {
      onClose();
    },
    onError: (error) => {
      alert(`Erreur lors de la mise à jour : ${error.message}`);
    }
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useForm<BookingFormData>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      travelerInfo: offer.bookings?.[0]?.travelerInfo || [],
      roomPreferences: offer.bookings?.[0]?.roomPreferences || [],
      specialRequests: offer.bookings?.[0]?.specialRequests || '',
    },
  });

  const onSubmit = (data: BookingFormData) => {
    const updatedBooking: BusinessOfferBooking = {
      id: offer.bookings?.[0]?.id || `booking-${Date.now()}`,
      offerId: offer.id,
      ...data,
      status: 'confirmed',
      createdAt: offer.bookings?.[0]?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    updateOffer({
      variables: {
        offerId: offer.id,
        input: {
          bookings: [updatedBooking],
        },
      },
    });
  };

  const addNewTraveler = () => {
    const currentTravelers = watch('travelerInfo');
    setValue('travelerInfo', [
      ...currentTravelers,
      {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        passport: {
          number: '',
          expiryDate: '',
          file: '',
        },
      },
    ]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Modifier les voyageurs</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Liste des voyageurs */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Liste des voyageurs</h3>
                <button
                  type="button"
                  onClick={addNewTraveler}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                >
                  <Plus className="w-5 h-5" />
                  Ajouter un voyageur
                </button>
              </div>

              <TravelerForm
                control={control}
                register={register}
                errors={errors}
                watch={watch}
                setValue={setValue}
              />
            </div>

            {/* Préférences de chambres */}
            <div>
              <h3 className="text-lg font-medium mb-4">Chambres</h3>
              {offer.bookings?.[0]?.roomPreferences.map((_, index) => (
                <div key={index} className="mb-4 p-4 border rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type de chambre
                      </label>
                      <select
                        {...register(`roomPreferences.${index}.type`)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="single">Simple</option>
                        <option value="double">Double</option>
                        <option value="triple">Triple</option>
                        <option value="suite">Suite</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre
                      </label>
                      <input
                        type="number"
                        min="1"
                        {...register(`roomPreferences.${index}.count`, { valueAsNumber: true })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Demandes spéciales */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Demandes spéciales
              </label>
              <textarea
                {...register('specialRequests')}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Mise à jour...' : 'Mettre à jour'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}